import type { HTMLAttributes, PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import { useCallback } from 'react';
import { KizenTypography } from '__app/typography';
import Button from '__components/Button';
import Input from '__components/Kizen/Input';

type ToolbarSearchInputProps = {
  loading?: boolean;
  placeholder: string;
  value: string | undefined;
  onChange(value: string): void;
};

const ToolbarContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, min-content) 1fr minmax(0, min-content);
  grid-auto-rows: 36px;
  align-items: center;
  margin: 20px 0;
`;

const SearchInput = styled(Input)`
  width: 200px;

  label {
    margin: 0;
  }

  .input-wrapper {
    margin: 0;
    background-color: var(--color-background-white);
  }

  input {
    min-width: 0;
    padding-right: 0;
  }
`;

const ToolbarTitle = styled(KizenTypography)`
  justify-self: center;
`;

const ToolbarRightButton = styled(Button)`
  margin: 0;
  margin-right: 15px;
`;

const LeftColumn = styled.div`
  display: flex;
  min-width: max(
    var(--settings-team-toolbar-left-column-width, 90px),
    var(--settings-team-toolbar-right-column-width, 90px)
  );
`;

const RightColumn = styled.div`
  display: flex;
  max-width: 355px;
  justify-content: end;
  min-width: max(
    var(--settings-team-toolbar-left-column-width, 90px),
    var(--settings-team-toolbar-right-column-width, 90px)
  );
`;

export const ToolbarSearchInput = (props: ToolbarSearchInputProps) => {
  return (
    <SearchInput pill isClearable type={Input.INPUT_TYPES.SEARCH} {...props} />
  );
};

const ToolbarRightColumn = (
  props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>
) => {
  const { children, ...rest } = props;

  const ref = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      if (node.clientWidth > 0) {
        node.parentElement?.style.setProperty(
          '--settings-team-toolbar-left-column-width',
          `${node.clientWidth}px`
        );
      }
    }
  }, []);

  return (
    <RightColumn ref={ref} {...rest}>
      {children}
    </RightColumn>
  );
};

export const ToolbarLeftColumn = (
  props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>
) => {
  const { children, ...rest } = props;

  const ref = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      if (node.clientWidth > 0) {
        node.parentElement?.style.setProperty(
          '--settings-team-toolbar-right-column-width',
          `${node.clientWidth}px`
        );
      }
    }
  }, []);

  return (
    <LeftColumn ref={ref} {...rest}>
      {children}
    </LeftColumn>
  );
};

export const Toolbar = {
  Container: ToolbarContainer,
  LeftColumn: ToolbarLeftColumn,
  RightButton: ToolbarRightButton,
  RightColumn: ToolbarRightColumn,
  SearchInput: ToolbarSearchInput,
  Title: ToolbarTitle,
};
